import React, { useState } from 'react'
import ModalPopup from '../../../components/ModalPopup/ModalPopup'
import { ButtonStyles, ButtonSize, ModalPopupSize, ImportMode } from '../../../lib/enums'
import './importModal.scss'

type ImportModalProps = {
  displayModal: (isDisplayed: boolean) => void
  isModalDisplayed: boolean
  handleImportFile: (file: File, importMode: ImportMode) => Promise<string | void>
  handleDownloadTemplate: () => void
}

const ImportModal = ({
  displayModal,
  isModalDisplayed,
  handleImportFile,
  handleDownloadTemplate
}: ImportModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [hasImportError, setHasImportError] = useState(false)

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      setSelectedFile(files[0])
      setValidationError(null)
      setHasImportError(false)
    }
  }

  const handleBrowseFiles = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsx,.xls'
    input.onchange = e => {
      const target = e.target as HTMLInputElement
      if (target.files && target.files[0]) {
        setSelectedFile(target.files[0])
        setValidationError(null)
        setHasImportError(false)
      }
    }
    input.click()
  }

  const handleApply = async () => {
    if (!selectedFile) {
      setValidationError('Please select a file to import')
      return
    }

    if (!isValidFile) {
      setValidationError('Please select a valid Excel file (.xlsx or .xls)')
      return
    }

    const error = await handleImportFile(selectedFile, ImportMode.Override)
    if (error) {
      setValidationError(error)
      setHasImportError(true)
    } else {
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setDragActive(false)
    setValidationError(null)
    setHasImportError(false)
    displayModal(false)
  }

  const isValidFile =
    selectedFile && (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) && !hasImportError

  return (
    <ModalPopup
      headline="Import Camera Groups"
      description="Upload a completed template file to import camera group configurations"
      size={ModalPopupSize.Medium}
      showModal={isModalDisplayed}
      onCloseButtonclick={handleClose}
      button1Style={ButtonStyles.Primary}
      button1Size={ButtonSize.Medium}
      button1Label="Apply"
      button1OnClickHandler={handleApply}
      button1Disabled={!isValidFile}>
      <div className="import-modal">
        {!isValidFile && (
          <div className="import-modal__template-link">
            Need a template?
            <button type="button" className="import-modal__download-template" onClick={handleDownloadTemplate}>
              Download Template
            </button>
          </div>
        )}

        <div
          className={`import-modal__drop-zone ${dragActive ? 'import-modal__drop-zone--active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}>
          <div className="import-modal__upload-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="14,2 14,8 20,8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="16"
                y1="13"
                x2="8"
                y2="13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="12"
                y1="17"
                x2="12"
                y2="9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {selectedFile ? (
            <div className="import-modal__file-selected">
              <p className="import-modal__file-name">{selectedFile.name}</p>
              <p className="import-modal__file-size">{formatFileSize(selectedFile.size)}</p>
              <button
                type="button"
                className="msds-btn msds-btn--secondary msds-btn--sm import-modal__button"
                onClick={handleBrowseFiles}>
                Choose Different File
              </button>
              {!isValidFile && selectedFile && !validationError && (
                <div className="import-modal__error import-modal__error--inline">
                  Please select a valid Excel file (.xlsx or .xls)
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="import-modal__drop-text">Drag & Drop files here</p>
              <p className="import-modal__or-text">or</p>

              <button
                type="button"
                className="msds-btn msds-btn--tertiary msds-btn--sm import-modal__button"
                onClick={handleBrowseFiles}>
                Browse Files
              </button>
              <p className="import-modal__or-text">Supported formats: .xlsx, .xls</p>
            </>
          )}

          {validationError && <div className="import-modal__error import-modal__error--inline">{validationError}</div>}
        </div>

        <div className="import-modal__options">
          {isValidFile && !hasImportError && (
            <div className="import-modal__warning">
              <div className="import-modal__warning-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="import-modal__warning-text">
                <strong>Warning:</strong> Importing will overwrite all existing camera group configurations.{' '}
                <strong> This action cannot be undone.</strong>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalPopup>
  )
}

export default ImportModal
